// Pure rolling-date helper used by the demo engine to emit one fixed-rate
// market per UI column label (1D, 2D, 1W, 2W, 3W, 1M, 2M, 3M, 1Q, 2Q, 3Q, 4Q).
//
// Mirrors the logic in src/utils/date.ts:getRollingDates so engine-generated
// markets line up 1:1 with the column headers rendered by useRollingDates()
// on the Markets page. Extracted here to avoid importing date.ts — which
// transitively pulls in utils/apis and would create an import cycle through
// the axios adapter that this engine is itself mocking.

import moment, { Moment } from 'moment';

export interface RollingMaturity {
  label: string;
  daysTo: number;
  date: Moment;
}

function getLastFridayOfMonth(m: Moment): Moment {
  const end = m.clone().endOf('month').startOf('day');
  const offset = (end.day() - 5 + 7) % 7;
  return end.subtract(offset, 'day');
}

function daysFromToday(to: Moment, ref: Moment): number {
  return Math.ceil(to.diff(ref, 'seconds') / 86_400);
}

export function getDemoRollingMaturities(): RollingMaturity[] {
  const ref = moment().utc().startOf('day');
  const dayOfWeek = ref.weekday();
  const weekOffset = dayOfWeek >= 5 || dayOfWeek === 0 ? 1 : 0;
  const monthOffset = ref.clone().isSameOrAfter(getLastFridayOfMonth(ref.clone())) ? 1 : 0;

  const d1D = ref.clone().add(1, 'day');
  const d2D = ref.clone().add(2, 'day');
  const d1W = ref.clone().add(weekOffset, 'week').startOf('isoWeek').add(4, 'day');
  const d2W = d1W.clone().add(1, 'week');
  const d3W = d2W.clone().add(1, 'week');
  const d1M = getLastFridayOfMonth(ref.clone().add(0 + monthOffset, 'month'));
  const d2M = getLastFridayOfMonth(ref.clone().add(1 + monthOffset, 'month'));
  const d3M = getLastFridayOfMonth(ref.clone().add(2 + monthOffset, 'month'));

  const month = ref.clone().add(monthOffset, 'month').month();
  const qMonths = [2, 5, 8, 11];
  let yearOffset = 0;
  let targetMonthIdx = -1;
  for (targetMonthIdx = 0; targetMonthIdx < qMonths.length; targetMonthIdx++) {
    if (month <= qMonths[targetMonthIdx]) break;
  }
  if (targetMonthIdx >= qMonths.length) targetMonthIdx = qMonths.length - 1;
  if (month === 0 && monthOffset === 1) { targetMonthIdx = 0; yearOffset = 1; }
  const targetMonth = qMonths[targetMonthIdx];
  const d1Q = getLastFridayOfMonth(ref.clone().add(yearOffset, 'year').month(targetMonth));
  const d2Q = getLastFridayOfMonth(d1Q.clone().add(3, 'month'));
  const d3Q = getLastFridayOfMonth(d1Q.clone().add(6, 'month'));
  const d4Q = getLastFridayOfMonth(d1Q.clone().add(9, 'month'));

  const items: RollingMaturity[] = [
    { label: '1D', daysTo: daysFromToday(d1D, ref), date: d1D },
    { label: '2D', daysTo: daysFromToday(d2D, ref), date: d2D },
    { label: '1W', daysTo: daysFromToday(d1W, ref), date: d1W },
    { label: '2W', daysTo: daysFromToday(d2W, ref), date: d2W },
    { label: '3W', daysTo: daysFromToday(d3W, ref), date: d3W },
    { label: '1M', daysTo: daysFromToday(d1M, ref), date: d1M },
    { label: '2M', daysTo: daysFromToday(d2M, ref), date: d2M },
    { label: '3M', daysTo: daysFromToday(d3M, ref), date: d3M },
    { label: '1Q', daysTo: daysFromToday(d1Q, ref), date: d1Q },
    { label: '2Q', daysTo: daysFromToday(d2Q, ref), date: d2Q },
    { label: '3Q', daysTo: daysFromToday(d3Q, ref), date: d3Q },
    { label: '4Q', daysTo: daysFromToday(d4Q, ref), date: d4Q },
  ];
  // Match date.ts: reverse first so uniqueByKey keeps the EARLIEST label when
  // two rolling buckets collide (e.g. 3W and 1M falling on the same date),
  // then re-sort ascending by daysTo.
  items.reverse();
  const seen = new Set<number>();
  const deduped = items.filter(it => {
    if (seen.has(it.daysTo)) return false;
    seen.add(it.daysTo);
    return true;
  });
  deduped.sort((a, b) => a.daysTo - b.daysTo);
  return deduped;
}
